import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Target, 
  Rocket, 
  FileText, 
  Users, 
  BarChart3, 
  Calendar,
  Clock,
  TrendingUp,
  CheckCircle
} from 'lucide-react';

const QuickActions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAllActions, setShowAllActions] = useState(false);

  // Define actions based on user role
  const getActionsForRole = () => {
    const baseActions = [
      {
        id: 'create-goal',
        title: 'Create Goal',
        description: 'Set a new performance goal',
        icon: Target,
        color: 'bg-blue-500 hover:bg-blue-600',
        textColor: 'text-blue-600',
        bgColor: 'bg-blue-50',
        action: () => navigate('/goals'),
        priority: 'high'
      },
      {
        id: 'create-okr',
        title: 'Create OKR',
        description: 'Define objectives and key results',
        icon: Rocket,
        color: 'bg-indigo-500 hover:bg-indigo-600',
        textColor: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        action: () => navigate('/okrs'),
        priority: 'high'
      },
      {
        id: 'update-progress',
        title: 'Update Progress',
        description: 'Log progress on your goals',
        icon: TrendingUp,
        color: 'bg-green-500 hover:bg-green-600',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50',
        action: () => navigate('/goals'),
        priority: 'medium'
      },
      {
        id: 'view-reviews',
        title: 'Performance Reviews',
        description: 'View and complete reviews',
        icon: FileText,
        color: 'bg-yellow-500 hover:bg-yellow-600',
        textColor: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        action: () => navigate('/performance-reviews'),
        priority: 'medium'
      }
    ];

    // Add role-specific actions
    if (['manager', 'hr', 'executive', 'admin'].includes(user?.role)) {
      baseActions.push(
        {
          id: 'team-overview',
          title: 'Team Overview',
          description: 'View team performance',
          icon: Users,
          color: 'bg-purple-500 hover:bg-purple-600',
          textColor: 'text-purple-600',
          bgColor: 'bg-purple-50',
          action: () => navigate('/team-dashboard'),
          priority: 'medium'
        },
        {
          id: 'analytics',
          title: 'Analytics',
          description: 'View performance insights',
          icon: BarChart3,
          color: 'bg-cyan-500 hover:bg-cyan-600',
          textColor: 'text-cyan-600',
          bgColor: 'bg-cyan-50',
          action: () => navigate('/performance-analytics'),
          priority: 'low'
        }
      );
    }

    if (['hr', 'admin'].includes(user?.role)) {
      baseActions.push(
        {
          id: 'review-cycles',
          title: 'Manage Cycles',
          description: 'Create and manage review cycles',
          icon: Calendar,
          color: 'bg-orange-500 hover:bg-orange-600',
          textColor: 'text-orange-600',
          bgColor: 'bg-orange-50',
          action: () => navigate('/review-cycles'),
          priority: 'medium'
        },
        {
          id: 'reports',
          title: 'Generate Reports',
          description: 'Create performance reports',
          icon: FileText,
          color: 'bg-pink-500 hover:bg-pink-600',
          textColor: 'text-pink-600',
          bgColor: 'bg-pink-50',
          action: () => navigate('/reports'),
          priority: 'low'
        }
      );
    }

    return baseActions.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  };

  const actions = getActionsForRole();
  const displayedActions = showAllActions ? actions : actions.slice(0, 6);

  // Quick stats for context-aware actions
  const quickStats = [
    {
      label: 'Goals Due This Week',
      value: 3,
      action: () => navigate('/goals'),
      urgent: true
    },
    {
      label: 'OKR Check-ins Due',
      value: 1,
      action: () => navigate('/okrs'),
      urgent: true
    },
    {
      label: 'Pending Reviews',
      value: 2,
      action: () => navigate('/performance-reviews'),
      urgent: false
    },
    {
      label: 'Team Members to Review',
      value: user?.role === 'manager' ? 5 : 0,
      action: () => navigate('/team-reviews'),
      urgent: false,
      roleRestricted: ['manager', 'hr', 'admin']
    }
  ].filter(stat => 
    !stat.roleRestricted || stat.roleRestricted.includes(user?.role)
  );

  const contextualSuggestions = [
    {
      condition: () => new Date().getDay() === 1, // Monday
      title: 'Weekly Planning',
      description: 'Plan your goals for the week',
      action: () => navigate('/goals'),
      icon: Calendar
    },
    {
      condition: () => new Date().getDate() === 1, // First of month
      title: 'Monthly Review',
      description: 'Review last month\'s performance',
      action: () => navigate('/performance-analytics'),
      icon: BarChart3
    },
    {
      condition: () => true, // Always show
      title: 'Quick Check-in',
      description: 'Update your goal progress',
      action: () => navigate('/goals'),
      icon: CheckCircle
    }
  ].filter(suggestion => suggestion.condition());

  return (
    <div className="space-y-6">
      {/* Urgent Actions / Context-Aware Stats */}
      {quickStats.some(stat => stat.urgent && stat.value > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Clock className="text-red-600" size={18} />
            <h3 className="font-medium text-red-800">Requires Attention</h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {quickStats
              .filter(stat => stat.urgent && stat.value > 0)
              .map((stat, index) => (
                <motion.button
                  key={index}
                  onClick={stat.action}
                  className="text-left p-3 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-red-800">{stat.label}</div>
                      <div className="text-red-600 text-xs">Click to view</div>
                    </div>
                    <div className="text-lg font-bold text-red-600">{stat.value}</div>
                  </div>
                </motion.button>
              ))
            }
          </div>
        </div>
      )}

      {/* Quick Actions Grid */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              <p className="text-sm text-gray-600">Fast access to common tasks</p>
            </div>
            {actions.length > 6 && (
              <button
                onClick={() => setShowAllActions(!showAllActions)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showAllActions ? 'Show Less' : 'Show All'}
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayedActions.map((action, index) => {
              const IconComponent = action.icon;
              
              return (
                <motion.button
                  key={action.id}
                  onClick={action.action}
                  className="group relative text-left p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-md transition-all duration-200"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${action.bgColor} group-hover:scale-110 transition-transform`}>
                      <IconComponent className={action.textColor} size={20} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 group-hover:text-gray-700">
                        {action.title}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {action.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Priority indicator */}
                  {action.priority === 'high' && (
                    <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contextual Suggestions */}
      {contextualSuggestions.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Suggested Actions</h3>
            <p className="text-sm text-gray-600">Based on your current context</p>
          </div>

          <div className="p-6">
            <div className="space-y-3">
              {contextualSuggestions.slice(0, 3).map((suggestion, index) => {
                const IconComponent = suggestion.icon;
                
                return (
                  <motion.button
                    key={index}
                    onClick={suggestion.action}
                    className="w-full flex items-center space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="p-2 bg-white rounded-lg">
                      <IconComponent className="text-gray-600" size={16} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{suggestion.title}</div>
                      <div className="text-sm text-gray-600">{suggestion.description}</div>
                    </div>
                    <div className="text-gray-400">→</div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Performance Summary */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Performance Summary</h3>
          <p className="text-sm text-gray-600">Overview of your current performance</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quickStats.map((stat, index) => (
              <motion.button
                key={index}
                onClick={stat.action}
                className="text-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className={`text-xl font-bold ${stat.urgent && stat.value > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {stat.value}
                </div>
                <div className="text-xs text-gray-600 mt-1">{stat.label}</div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;